import React, { useContext, useEffect, useState } from 'react'
import { Grid } from '../common/grid/Grid'
import { GridItem } from '../common/grid/GridItem'
import { IComplianceState } from '../../interfaces/IComplianceState'
import {
   Activity,
   Components,
   ConsequenceAndProbabilityRisk,
   DemandAnalysisDashboardDTO,
   FinancialYear,
   FinancialYearDashboardGraphDTO,
   RiskAnalysis,
   RiskManagementModules,
} from '../../api/schemas/schema'
import { IListState } from '../../interfaces/IListState'
import { DashboardComplianceCards } from './DashboardComplianceCards'
import { DashboardCard } from './DashboardCard'
import { makeStyles, Spinner } from '@fluentui/react-components'
import { useNavigate } from 'react-router-dom'
import { BiggestRisksCard } from './DashboardStandard/BiggestRisksCard'
import { RiskEconomyCard } from './DashboardStandard/RiskEconomyCard'
import { ActivitiesCard } from './DashboardStart/ActivitiesCard'
import { getBiggestRisks, getDemandAnalysisDashboardData, getLatestFinancialYears, getLatestSwotAnalysis } from '../../helpers/dashboardHelper'
import { DataContext } from '../../context/DataContext'
import { useHasModule } from '../../helpers/useHasModule'
import { RiskManagementCard } from './DashboardStart/RiskManagementCard'
const useStyles = makeStyles({
   card: {
      '@media screen and (max-width: 1450px)': {
         gridColumn: 'span 12',
      },
   },
})

type DashboardModulesProps = {
   setComplianceState: React.Dispatch<React.SetStateAction<IComplianceState>>
   setActivityModalState: React.Dispatch<
      React.SetStateAction<{
         isOpen: boolean
         selectedActivity?: Activity
      }>
   >
   setRiskManagementState: React.Dispatch<React.SetStateAction<IListState<RiskAnalysis>>>
   setRiskEconomyState: React.Dispatch<React.SetStateAction<IListState<FinancialYear>>>
   latestActivities: Activity[] | undefined
}

export const DashboardModules = ({
   setComplianceState,
   setActivityModalState,
   setRiskManagementState,
   setRiskEconomyState,
   latestActivities,
}: DashboardModulesProps) => {
   const classes = useStyles()
   const navigate = useNavigate()
   const dataContext = useContext(DataContext)
   const { organisation } = dataContext.state
   const [latestFinancialYears, setLatestFinancialYears] = useState<FinancialYearDashboardGraphDTO[]>([])
   const [biggestRisks, setBiggestRisks] = useState<ConsequenceAndProbabilityRisk[]>([])
   const [latestSwotAnalysis, setLatestSwotAnalysis] = useState<RiskAnalysis | undefined>(undefined)
   const [dashboardData, setDashboardData] = useState<DemandAnalysisDashboardDTO>()
   const [isLoading, setIsLoading] = useState(true)

   useEffect(() => {
      if (!organisation?.id) return
      const ac = new AbortController()
      Promise.all([
         getBiggestRisks(dataContext, setBiggestRisks, ac.signal),
         getLatestFinancialYears(dataContext, setLatestFinancialYears, ac.signal),
         getLatestSwotAnalysis(dataContext, setLatestSwotAnalysis, ac.signal),
         getDemandAnalysisDashboardData(dataContext, setDashboardData, ac.signal),
      ]).finally(() => { if (!ac.signal.aborted) setIsLoading(false) })
      return () => ac.abort()
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [organisation?.id])

   const { checkAccess } = useHasModule()

   const hasAccess = {
      compliance: checkAccess([Components.DEMAND_COMPLIANCE]),
      biggestRisks: checkAccess([Components.RISK_MANAGEMENT]) && checkAccess([RiskManagementModules.CONSEQUENCE_AND_PROBABILITY_ANALYSIS]),
      riskEconomy: checkAccess([Components.RISK_ECONOMY]),
      calendar: checkAccess([Components.CALENDAR]),
      swot: checkAccess([Components.RISK_MANAGEMENT]) && checkAccess([RiskManagementModules.SWOT_ANALYSIS]),
   }

   const canShowAllFive = Object.values(hasAccess).filter(Boolean).length === 5

   const modules = {
      ...hasAccess,
      showSWOT: hasAccess.swot && !canShowAllFive,
      showCompliance: hasAccess.compliance,
      showBiggestRisks: hasAccess.biggestRisks,
      showRiskEconomy: hasAccess.riskEconomy,
      showCalendar: hasAccess.calendar,
   }

   const shortModules = [
      { key: 'compliance', show: modules.showCompliance },
      { key: 'biggestRisks', show: modules.showBiggestRisks },
   ].filter((m) => m.show)

   const tallModules = [
      { key: 'riskEconomy', show: modules.showRiskEconomy },
      { key: 'calendar', show: modules.showCalendar },
      { key: 'swot', show: modules.showSWOT },
   ].filter((m) => m.show)

   const getSize = (key: string) => {
      const isShort = ['compliance', 'biggestRisks'].includes(key)

      if (isShort && shortModules.length === 1) {
         return '12/12'
      }

      const gridItems = [...(shortModules.length > 1 ? shortModules : []), ...tallModules]

      const totalGridItems = gridItems.length
      const indexInGrid = gridItems.findIndex((m) => m.key === key)

      // Fallback to full width if something goes wrong with the index
      if (indexInGrid === -1) return '12/12'

      // If last item is odd, make it full width
      if (indexInGrid === totalGridItems - 1 && totalGridItems % 2 !== 0) {
         return '12/12'
      }

      return '6/12'
   }
   if (organisation?.modules === undefined) {
      return <Spinner size="large" style={{ margin: 'auto' }} />
   }

   return (
      <>
         <Grid gap="XXXL">
            {/* COMPLIANCE*/}
            {modules.showCompliance && (
               <GridItem size={getSize('compliance')} className={classes.card}>
                  <DashboardComplianceCards setComplianceState={setComplianceState} dashboardData={dashboardData} loading={isLoading} />
               </GridItem>
            )}

            {/* BIGGEST RISKS */}
            {modules.showBiggestRisks && (
               <GridItem size={getSize('biggestRisks')} className={classes.card}>
                  <DashboardCard height100 color="beige" resetCss={!isLoading && biggestRisks.length === 0} loading={isLoading}>
                     <BiggestRisksCard setRiskManagementState={setRiskManagementState} biggestRisks={biggestRisks} />
                  </DashboardCard>
               </GridItem>
            )}

            {/* RISK ECONOMY */}
            {modules.showRiskEconomy && (
               <GridItem size={getSize('riskEconomy')} className={classes.card}>
                  <DashboardCard color="beige" bigCard resetCss={!isLoading && latestFinancialYears.length === 0} loading={isLoading}>
                     <RiskEconomyCard setRiskEconomyState={setRiskEconomyState} latestFinancialYears={latestFinancialYears} />
                  </DashboardCard>
               </GridItem>
            )}

            {/* CALENDAR */}
            {modules.showCalendar && (
               <GridItem size={getSize('calendar')} className={classes.card}>
                  <DashboardCard color="beige" bigCard resetCss={latestActivities !== undefined && latestActivities.length === 0} loading={latestActivities === undefined}>
                     <ActivitiesCard setActivityModalState={setActivityModalState} latestActivities={latestActivities ?? []} />
                  </DashboardCard>
               </GridItem>
            )}

            {/* SWOT ANALYSIS */}
            {modules.showSWOT && (
               <GridItem size={getSize('swot')} className={classes.card}>
                  <DashboardCard
                     color="beige"
                     bigCard
                     resetCss={!isLoading && !latestSwotAnalysis}
                     loading={isLoading}
                     onClick={() => (latestSwotAnalysis ? navigate(`/risk-management/${latestSwotAnalysis.id}`) : null)}
                  >
                     <RiskManagementCard latestSwotAnalysis={latestSwotAnalysis} setRiskManagementState={setRiskManagementState} />
                  </DashboardCard>
               </GridItem>
            )}
         </Grid>
      </>
   )
}
